<template>
  <div class="main-content">
    
    <div class="summary-container">
      <h2>Summary</h2>

      <div class="summary-learners">
          <p><span>Total learners in this section: </span>{{ total_learners }}</p>
          <p><span>Total modules in this section: </span>{{ total_modules }}</p>
          <p  v-if="this.total_modules > 1"><span>Total learners who have completed all modules: </span>{{ total_completed_learners }} [{{ total_completed_percent}}%]</p>
          <p  v-else><span>Total learners who have completed this module: </span>{{ total_completed_learners }} [{{ total_completed_percent}}%]</p>
      </div>

      <div class="modules_completion" v-if="this.total_modules > 1">
        <table class="modules_completion_table">
          <thead>
            <th>Module</th>
            <th>Completion rate</th>
            <th>Learners completed</th>
          </thead>
          <tbody>
            <tr
              v-for="(module) in modules_summary" 
              :key="module.id"
            >
              <td>{{ module.module_name }}</td>
              <td>{{ module.completion_rate }}%</td>
              <td>{{ module.completed_count }}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
        
      </div>
    </div>
    <div class="details-container">
      <h2>Learner Details</h2>
      <div class="details-header">
        <input 
          class="search-input" 
          type="text" 
          placeholder="Search learners" 
          v-model="searchQuery" 
          @input="debouncedSearch(searchQuery)"
        />
        <div class="export-csv-container">
          <button class="btn" @click="downloadTableData()">{{ downloadButtonText }}</button>
        </div>
      </div>
      <div ref="completionTable" id="main-data-table"></div>
    </div>
  </div>
</template>
<script>
import api from "../services/api";
import { Tabulator, FilterModule, DownloadModule, ExportModule, PageModule , AjaxModule} from 'tabulator-tables';
import dayjs from "dayjs";
import { utilFunctionService } from "@/utils/utils.service";
import XLSX from "xlsx";
import _ from 'lodash';
window.XLSX = XLSX;
Tabulator.registerModule([FilterModule, DownloadModule, ExportModule, PageModule, AjaxModule]);

export default {
  name: "Completion-Reporting",
  data() {
    return {
      avg_grades: [],
      options: {},
      searchQuery: "",
      date: new Date(),
      course_title: "",
      course_start: "",
      course_end: "",
      section: "",
      total_learners: '',
      total_modules: '',
      total_completed_learners: '',
      total_completed_percent: '',
      users: [],
      modules: [],
      learners: [],
      columns_data: [], 
      tabulator: null
    };
  },
  created() {
    // Get section data first
    this.section = JSON.parse(localStorage.getItem("section"));
    utilFunctionService.showLoader();
    this.debouncedSearch = _.debounce(this.searchLearner, 300);

    api.getCompletionReportData(this.section.id)
      .then(this.handleInitialDataLoad)
      .catch((error) => {
        console.error("Error loading initial data:", error);
        utilFunctionService.hideLoader();
      });
  },
  computed: {
    downloadButtonText() {
      return this.searchQuery && this.searchQuery.trim() !== '' 
        ? `Download Searched Learner Data` 
        : `Download All Learner Data`;
    }
  },
  methods: {
    searchLearner(learnerQuery) {
      if (!this.tabulator) return;
      
      // Save the search query
      this.searchQuery = String(learnerQuery || '').trim();
      
      // Update the ajaxParams in the Tabulator options
      if (!this.tabulator.options.ajaxParams) {
        this.tabulator.options.ajaxParams = {};
      }
      
      // Set the search parameter
      this.tabulator.options.ajaxParams.search = this.searchQuery;
      this.tabulator.options.ajaxParams.page = 1; // Reset to page 1
      
      // No need to call setPage or setData separately
      // Updating the filter/params will automatically trigger the request
      // according to the Stack Overflow answer
      
      // Just need to refresh the data once
      this.tabulator.setData();
    },
    // Updated downloadTableData method that works with pagination
    downloadTableData() {
      if (!this.tabulator) return;
      
      // Create download URL with search parameter if it exists
      let downloadURL = `${process.env.VUE_APP_URL}instructor/v1/sections/${this.section.id}/reports_completion/download_all_learner_data`;
      
      // Add search query if it exists
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        downloadURL += `?search=${encodeURIComponent(this.searchQuery)}`;
      }
      
      // Get auth token
      const accessToken = utilFunctionService.getAuthToken();
      
      // Show loader
      utilFunctionService.showLoader();
      
      // Use fetch to get the file with the authentication header
      fetch(downloadURL, {
        method: 'GET',
        headers: {
          "Authorization": `${accessToken}`,
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.blob();
      })
      .then(blob => {
        // Create a URL for the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary link and click it to initiate download
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        
        // Get filename from content-disposition header if available, otherwise use default
        const filename = this.getFilenameFromSearchState();
        a.download = filename;
        
        // Append to the document and trigger click
        document.body.appendChild(a);
        a.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        utilFunctionService.hideLoader();
      })
      .catch(error => {
        console.error('Error downloading file:', error);
        utilFunctionService.hideLoader();
      });
    },

    // Helper method to generate appropriate filename based on search state
    getFilenameFromSearchState() {
      const baseFilename = this.section.attributes.name || "Completion Report";
      const dateStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        return `${baseFilename} - Filtered Results (${this.searchQuery}) - ${dateStr}.xlsx`;
      } else {
        return `${baseFilename} - All Learners - ${dateStr}.xlsx`;
      }
    },
    handleInitialDataLoad(res) {
      // Save basic data from the initial API call
      this.processInitialData(res.data);
      
      // Setup columns based on the data
      this.setupColumnsData();
      
      // Wait for the DOM to be ready before initializing Tabulator
      this.$nextTick(() => {
        this.initializeTabulator();
      });
    },
    processInitialData(data) {
      // Process course metadata
      this.course_title = data.data.attributes.course_name;
      this.total_learners = data.data.attributes.total_learners_count;
      this.total_completed_learners = data.data.attributes.total_learners_completed_all_modules;
      this.total_completed_percent = data.data.attributes.percent_learners_finished_all_modules;
      this.total_modules = data.data.attributes.total_modules;
      this.modules_summary = data.data.attributes.modules_summary;
      
      this.currentPage = data.meta.current_page;
      this.totalPages = data.last_page;

      // Store initial users data for the first page
      this.users = data.data.attributes.section_learners;
      
      // Format dates
      if (data.data.attributes.course_start_date) {
        this.course_start = dayjs(data.data.attributes.course_start_date).format("dddd, MMMM D, YYYY");
      } else {
        this.course_start = "N/A";
      }
      
      if (data.data.attributes.course_end_date) {
        this.course_end = dayjs(data.data.attributes.course_end_date).format("dddd, MMMM D, YYYY");
      } else {
        this.course_end = "N/A";
      }
    },
    setupColumnsData() {
      // Build columns structure based on data
      this.columns_data = [
        {title: "Name", field: "learner_name"},
        {title: "Email", field: "learner_email"}, 
        {title: "Overall", 
          columns: [
            {title: "Start Date", field: "learner_start_date"},
            {title: "End Date", field: "learner_completion_date"},
            {title: "% Complete", field: "percent_complete"}
          ]
        }
      ];
      
      // Add module columns if we have multiple modules
      if (this.total_modules >= 1 && this.users && this.users.length > 0) {
        // Get the first user to determine module structure
        const firstUser = this.users[0];
        
        // Check if we have learner_modules_data
        if (firstUser.learner_modules_data) {
          firstUser.learner_modules_data.slice(0, this.total_modules).forEach((module_data, index) => {
            const moduleIndex = index + 1;
            const moduleName = firstUser[`learner_module_${moduleIndex}_name`];
            
            const moduleColumn = {
              title: moduleName,
              columns: [
                { title: "Start Date", field: `learner_module_${moduleIndex}_start_date` },
                { title: "End Date", field: `learner_module_${moduleIndex}_end_date` },
                { title: "% Complete", field: `learner_module_${moduleIndex}_percent_complete` }
              ]
            };
            
            this.columns_data.push(moduleColumn);
          });
        }
      }
    },
    initializeTabulator() {
      // Make sure we have the DOM reference
      if (!this.$refs.completionTable) {
        console.error("DOM element for Tabulator not found!");
        return;
      }
      
      const tabulatorDataURL = `${process.env.VUE_APP_URL}instructor/v1/sections/${this.section.id}/reports_completion/learner_completion_data`;
      let accessToken = utilFunctionService.getAuthToken();
      const tabulatorOptions = {
        pagination: true,
        paginationMode: "remote", 
        paginationSizeSelector: [5, 10, 20, 50],
        paginationSize: 20,
        paginationInitialPage: this.currentPage,
        ajaxURL: tabulatorDataURL,
        ajaxConfig: {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${accessToken}`,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Methods":
              "GET, POST, PATCH, PUT, DELETE, OPTIONS",
            "Access-Control-Allow-Headers":
              "Origin, Content-Type, X-Auth-Token"
          }
        },
        ajaxParams: {
          search: this.searchQuery || '',
          page: 1,
        },
        ajaxResponse: function(url, params, response) {
          if (!response.data || !response.data.attributes || !response.data.attributes.section_learners) {
            // Return an empty data set with the required format
            return {
              data: [],
              last_page: response.data.meta?.last_page,
              current_page: response.meta?.current_page || 1,
              total_records: response.meta?.total_records || 0
            };
          }
          return {
            data: response.data.attributes.section_learners,
            last_page: response.last_page,
            current_page: response.meta.current_page,
            total_records: response.meta.total_records
          };
        },
        paginationDataReceived: {
          "data": "data.data.attributes.section_learners",
          "last_page": "last_page" 
        },
        dataReceiveParams: {
          "last_page": "last_page"
        },
        autoColumns: false,
        columnHeaderVertAlign: "bottom",
        layout: this.total_modules > 1 ? "fitData" : "fitColumns",
        layoutColumnsOnNewData: true,
        resizableColumns: true,
        height: "600px",
        persistence: {
          sort: true,
          filter: true,
          columns: true,
        },
        selectable: true,
        downloadConfig: {
          columnHeaders: true,
          columnGroups: true,
          rowGroups: false,
          columnCalcs: false,
          dataTree: true
        },
        columns: this.columns_data,
      };
      
      // Create the Tabulator instance
      this.tabulator = new Tabulator(this.$refs.completionTable, tabulatorOptions);
      this.tabulatorInitialized = true;
      
      // Add event listeners for debugging
      this.tabulator.on("tableBuilt", () => {
        utilFunctionService.hideLoader();
      });
      
      // Handle any errors
      this.tabulator.on("error", (error) => {
        console.error("Tabulator error:", error);
      });
      
    },
  },
  watch: {
    users: {
      handler(newVal) {
        // If tabulator is initialized and we have data, update the table
        if (this.tabulator && newVal.length > 0) {
          this.tabulator.setData(newVal);
        }
      },
      deep: true
    }
  }
};
</script>


<style lang="scss" scoped>
@import "tabulator-tables/dist/css/tabulator.css";
.main-content {
  padding: 0;
  width: 100%;
}

.completion-grid {
  width: 100%;
  height: 500px;
  margin-top: 32px;
}
.selection-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  .custom-select {
    width: 35%;
  }
}
.summary-container {
  margin-bottom: 64px;

  p {
    margin-bottom: 16px;
    span {
      font-weight: bold;
    }
  }

  h2 {
    font-family: 'robotobold', sans-serif;
    font-size: 18px;
    text-transform: capitalize;
  }
  
  .summary {
    margin: 2em 0 0 0;
    
  }

  .modules_completion {
    padding-top: 2em;
    .modules_completion_table {
      width: 100%;
      padding-top: 1em;
      thead {
        border-bottom: 1px solid #333;
        th {
          padding-bottom: 0.5em;
        }
      }
      tr {
        td {
          padding-right: 1em;
          padding-top: 0.5em;
          width: 20%;
        }
      }
    
  }
}
  .summary-learners {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding-top: 1em;
  }
}
.details-container {
  h2 {
    font-family: 'robotobold', sans-serif;
    font-size: 18px;
    text-transform: capitalize;
  }
  h3 {
    font-size: 16px;
    font-family: 'robotobold', sans-serif;
    text-transform: capitalize;
  }
  .details-header {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: flex-start;
    .search-input {
      color: #4a5568;
      padding: 0.5rem 0.5rem;
      border-width: 1px;
      border-radius: 0.25rem;
      background-color: #fff;
      margin-left: 16px;
    }
  }
  .export-csv-container {
    display: flex;
    flex-direction: column;
    margin-left: 16px;
  }
  .datePickerBtn {
    color: #3182ce;
    padding: 0.5rem;
    border-width: 1px;
    border-radius: 0.25rem;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-color: #bee3f8;
    background-color: #ebf8ff;
    svg {
      fill: currentColor;
      width: 1rem;
      height: 1rem;
    }
  }
  .datePickerInput {
    color: #4a5568;
    padding: 0.5rem 0.5rem;
    border-width: 1px;
    border-radius: 0.25rem;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    background-color: #fff;
  }
}
.export-csv-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  .btn {
    border: 3px solid #3dbc9e;
    background: #fff;
  }
  .settings-btn {
    margin-top: 16px;
    border: none;
    font-weight: bold;
    background: none;
  }
}

.tabulator {
  margin-top: 16px;
  background: #fff;
  font-size: 1em;
  
}


</style>
